import React, { useLayoutEffect, useMemo, useRef, useEffect, useState } from "react";
import { ArrowLeft20Regular, Pin20Regular } from "@fluentui/react-icons";
import { labelForUserKey, type AppUser } from "../../services/userService";
import {
  chatService,
  type ChatConversation,
  type ChatMember,
  type ChatMessage,
} from "../../services/chatService";
import { useChatMessages } from "../../hooks/useChatMessages";
import { useChat } from "../../context/ChatContext";
import { usePresence } from "./usePresence";
import { PresenceDot } from "./PresenceDot";
import { MessageBubble } from "./MessageBubble";
import { ChatComposer, type MentionMember } from "./ChatComposer";
import { GroupMembersDialog } from "./GroupMembersDialog";
import { TypingIndicator } from "./TypingIndicator";
import { InitialsAvatar, MiniSpinner } from "./chatUi";
import { AUB, INK, INK2, LINE, PLUM, THREAD_BG, CHAT_PAD_X, fmtDayLabel, gruppenName } from "./chatTheme";
import { alleDokumente } from "../windowManager/browserFenster/fensterRegister";

type Props = {
  conversation: ChatConversation;
  members: ChatMember[];
  users: AppUser[];
  currentUserId: string | null;
  currentUserName: string | null;
  onBack?: () => void;
  /** Darf der Nutzer Mitglieder pflegen? (Admin/GFGL/Teamleiter — vom Server). */
  canManage?: boolean;
  /** Darf der Nutzer die Gruppe löschen? (Admin/GFGL — vom Server). */
  canDelete?: boolean;
  /** Mitglieder haben sich geändert → Detail + Liste neu laden. */
  onMembersChanged?: () => void;
  /** Gruppe wurde gelöscht → zurück zur Übersicht. */
  onGroupDeleted?: () => void;
};

export const ChatThread: React.FC<Props> = ({
  conversation,
  members,
  users,
  currentUserId,
  currentUserName,
  onBack,
  canManage = false,
  canDelete = false,
  onMembersChanged,
  onGroupDeleted,
}) => {
  const chat = useChat();
  const cm = useChatMessages(conversation.id, currentUserId, currentUserName);
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  const [pins, setPins] = useState<ChatMessage[]>([]);
  const [membersOpen, setMembersOpen] = useState(false);
  const [pinsOpen, setPinsOpen] = useState(false);
  const pinsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setReplyTo(null);
    setPinsOpen(false);
    setMembersOpen(false);
  }, [conversation.id]);

  // Pins-Dropdown schließen bei Klick außerhalb / Escape.
  useEffect(() => {
    if (!pinsOpen) return;
    chatService.listPins(conversation.id).then(setPins).catch(() => setPins([]));
    const onDoc = (e: MouseEvent) => {
      if (pinsRef.current && !pinsRef.current.contains(e.target as Node)) setPinsOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setPinsOpen(false);
    alleDokumente.addEventListener("pointerdown", onDoc);
    alleDokumente.addEventListener("keydown", onKey);
    return () => {
      alleDokumente.removeEventListener("pointerdown", onDoc);
      alleDokumente.removeEventListener("keydown", onKey);
    };
  }, [pinsOpen, conversation.id]);

  const nameOf = useMemo(() => {
    return (key: string) => {
      const lc = key.trim().toLowerCase();
      const u = users.find(
        (x) =>
          x.id.toLowerCase() === lc ||
          (x.email ?? "").toLowerCase() === lc ||
          (x.username ?? "").toLowerCase() === lc,
      );
      if (u) {
        const fl = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
        return fl || u.name || u.username || u.email || labelForUserKey(key, users);
      }
      return labelForUserKey(key, users);
    };
  }, [users]);

  const isDm = conversation.type === "dm";
  const otherId = isDm ? members.find((m) => m.userId !== currentUserId)?.userId ?? null : null;
  const presence = usePresence(otherId ? [otherId] : []);

  const title = isDm ? (otherId ? nameOf(otherId) : "Direktnachricht") : gruppenName(conversation.title) || "Team";

  const otherMembers = useMemo(
    () => members.filter((m) => m.userId !== currentUserId && !m.leftAt),
    [members, currentUserId],
  );

  // Erwähnbare Mitglieder fürs @-Autocomplete im Composer: aktive Mitglieder
  // ohne den eigenen Nutzer, mit Anzeigename + Kürzel (Username/E-Mail-Präfix).
  const mentionMembers = useMemo<MentionMember[]>(() => {
    return otherMembers.map((m) => {
      const lc = m.userId.trim().toLowerCase();
      const u = users.find(
        (x) =>
          x.id.toLowerCase() === lc ||
          (x.email ?? "").toLowerCase() === lc ||
          (x.username ?? "").toLowerCase() === lc,
      );
      const kuerzel = u?.username || u?.email?.split("@")[0] || null;
      return { userId: m.userId, name: nameOf(m.userId), kuerzel };
    });
  }, [otherMembers, users, nameOf]);

  const subtitle = useMemo(() => {
    if (!isDm) return `${otherMembers.length + 1} Mitglieder`;
    const st = otherId ? presence[otherId] : undefined;
    return st === "online" ? "Online" : st === "away" ? "Abwesend" : "Offline";
  }, [isDm, otherMembers.length, otherId, presence]);

  // --- Lesebestätigung (immer an): DM „Gelesen", sonst „Gelesen von N/M". ---
  const readHorizons = useMemo(() => {
    const map: Record<string, string> = {};
    for (const m of otherMembers) if (m.lastReadMessageId) map[m.userId] = m.lastReadMessageId;
    for (const [uid, rid] of Object.entries(cm.otherReads)) {
      if (uid !== currentUserId && rid) map[uid] = rid;
    }
    return map;
  }, [otherMembers, cm.otherReads, currentUserId]);

  const lastOwnId = useMemo(() => {
    let best: string | null = null;
    for (const m of cm.messages) if (m.authorUserId === currentUserId && m.seq && m.id) best = m.id;
    return best;
  }, [cm.messages, currentUserId]);

  const readReceiptLabel = useMemo(() => {
    if (!lastOwnId) return null;
    const n = otherMembers.filter((m) => {
      const r = readHorizons[m.userId];
      return r && Number(r) >= Number(lastOwnId);
    }).length;
    if (n <= 0) return null;
    return isDm ? "Gelesen" : `Gelesen von ${n}/${otherMembers.length}`;
  }, [lastOwnId, otherMembers, readHorizons, isDm]);

  // --- Scroll-Verhalten ---
  const scrollRef = useRef<HTMLDivElement>(null);
  const atBottomRef = useRef(true);
  const pendingPrependHeight = useRef<number | null>(null);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (pendingPrependHeight.current != null) {
      el.scrollTop = el.scrollHeight - pendingPrependHeight.current;
      pendingPrependHeight.current = null;
      return;
    }
    if (atBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [cm.messages, cm.ready]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    atBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (el.scrollTop < 60 && cm.hasMore && !cm.loadingOlder) {
      pendingPrependHeight.current = el.scrollHeight;
      void cm.loadOlder();
    }
  };

  const newestRealId = useMemo(() => {
    for (let i = cm.messages.length - 1; i >= 0; i--) if (cm.messages[i].seq) return cm.messages[i].id;
    return null;
  }, [cm.messages]);

  useEffect(() => {
    if (!cm.ready || !newestRealId) return;
    cm.markRead(newestRealId);
    chat.markConversationRead(conversation.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cm.ready, newestRealId, conversation.id]);

  const typingNames = cm.typingUserIds.map((id) => nameOf(id));

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: `10px ${CHAT_PAD_X}px`,
          borderBottom: `1px solid ${LINE}`,
          flexShrink: 0,
        }}
      >
        {onBack && (
          <button type="button" onClick={onBack} aria-label="Zurück" style={{ border: "none", background: "transparent", cursor: "pointer", color: INK }}>
            <ArrowLeft20Regular />
          </button>
        )}
        {/* Gruppen: die Kopfzeile öffnet die Mitgliederverwaltung (PO 29.08.2026).
            DMs bleiben ein statischer Kopf — dort gibt es nichts zu verwalten. */}
        <button
          type="button"
          disabled={isDm}
          onClick={isDm ? undefined : () => setMembersOpen(true)}
          title={isDm ? undefined : "Mitglieder anzeigen"}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            minWidth: 0,
            flex: 1,
            border: "none",
            background: "transparent",
            padding: 0,
            textAlign: "left",
            cursor: isDm ? "default" : "pointer",
            fontFamily: "inherit",
          }}
        >
          <InitialsAvatar
            name={title}
            size={40}
            square={!isDm}
            bg={!isDm ? PLUM : undefined}
            userId={isDm ? otherId : undefined}
            presence={otherId ? <PresenceDot status={presence[otherId]} size={11} /> : undefined}
          />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 800, color: INK, fontSize: 16, overflowWrap: "anywhere", fontFamily: "'Playfair Display', Georgia, serif" }}>
              {title}
            </div>
            <div style={{ color: INK2, fontSize: 12 }}>{subtitle}</div>
          </div>
        </button>

        <div style={{ marginLeft: "auto", position: "relative" }} ref={pinsRef}>
          <button
            type="button"
            aria-label="Angeheftete Nachrichten"
            title="Angeheftet"
            onClick={() => setPinsOpen((o) => !o)}
            style={{ width: 36, height: 36, borderRadius: 10, border: `1px solid ${LINE}`, background: pinsOpen ? "#faf6fb" : "#fff", cursor: "pointer", color: AUB, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
          >
            <Pin20Regular />
          </button>
          {pinsOpen && (
            <div
              style={{
                position: "absolute",
                top: 44,
                right: 0,
                zIndex: 40,
                width: 320,
                maxHeight: 360,
                overflowY: "auto",
                background: "#fff",
                border: `1px solid ${LINE}`,
                borderRadius: 12,
                boxShadow: "0 12px 32px rgba(20,8,20,0.16)",
                padding: 12,
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 6, color: INK }}>Angeheftet</div>
              {pins.length === 0 ? (
                <div style={{ color: INK2, fontSize: 13 }}>Keine angehefteten Nachrichten.</div>
              ) : (
                pins.map((p) => (
                  <div key={p.id} style={{ borderTop: `1px solid ${LINE}`, padding: "6px 0", fontSize: 13 }}>
                    <div style={{ fontWeight: 600, fontSize: 12, color: INK }}>{nameOf(p.authorUserId)}</div>
                    <div style={{ color: INK, overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}>{p.body || "(Anhang)"}</div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Nachrichten */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        role="log"
        aria-live="polite"
        aria-relevant="additions"
        aria-label="Nachrichtenverlauf"
        style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: `8px ${CHAT_PAD_X}px 4px`, background: THREAD_BG }}
      >
        {!cm.ready ? (
          <div style={{ padding: 24, textAlign: "center" }}>
            <MiniSpinner label="Lädt…" />
          </div>
        ) : cm.messages.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", color: INK2, fontSize: 13, fontStyle: "italic" }}>
            Noch keine Nachrichten — schreib die erste.
          </div>
        ) : (
          <>
            {cm.loadingOlder && (
              <div style={{ textAlign: "center", padding: 6 }}>
                <MiniSpinner />
              </div>
            )}
            {cm.messages.map((m, i) => {
              const prev = cm.messages[i - 1];
              const newDay = !prev || fmtDayLabel(prev.createdAt) !== fmtDayLabel(m.createdAt);
              const isOwn = m.authorUserId === currentUserId;
              const showHeader = newDay || !prev || prev.authorUserId !== m.authorUserId;
              return (
                <React.Fragment key={m.id || m.clientMsgId}>
                  {newDay && (
                    <div style={{ textAlign: "center", margin: "12px 0 4px", fontSize: 11.5, color: INK2 }}>
                      <span style={{ background: "#f3eef5", borderRadius: 999, padding: "2px 12px" }}>
                        {fmtDayLabel(m.createdAt)}
                      </span>
                    </div>
                  )}
                  <MessageBubble
                    message={m}
                    isOwn={isOwn}
                    authorName={nameOf(m.authorUserId)}
                    showHeader={showHeader}
                    reactions={cm.reactions[m.id] ?? []}
                    onEdit={cm.edit}
                    onDelete={cm.remove}
                    onToggleReaction={cm.toggleReaction}
                    onRetry={cm.retry}
                    onReply={(msg) => setReplyTo(msg)}
                    onTogglePin={cm.togglePin}
                    readReceiptLabel={m.id === lastOwnId ? readReceiptLabel : null}
                  />
                </React.Fragment>
              );
            })}
          </>
        )}
      </div>

      {/* Tippanzeige + Eingabe — fluchtet horizontal mit Header und Nachrichtenfläche. */}
      <div style={{ flexShrink: 0, padding: `0 ${CHAT_PAD_X}px 10px` }}>
        <TypingIndicator names={typingNames} />
        <ChatComposer
          conversationId={conversation.id}
          onTyping={cm.notifyTyping}
          members={isDm ? undefined : mentionMembers}
          placeholder={`Nachricht an ${title}…`}
          replyTo={
            replyTo
              ? {
                  id: replyTo.id,
                  authorName: nameOf(replyTo.authorUserId),
                  body: replyTo.deletedAt ? "" : (replyTo.body || "").slice(0, 120),
                }
              : null
          }
          onCancelReply={() => setReplyTo(null)}
          onSubmit={async ({ text, attachments, replyToId, mentionedUserIds }) => {
            await cm.send(text, mentionedUserIds, attachments, replyToId);
          }}
        />
      </div>

      {membersOpen && !isDm && (
        <GroupMembersDialog
          conversation={conversation}
          members={members.filter((m) => !m.leftAt)}
          users={users}
          currentUserId={currentUserId}
          nameOf={nameOf}
          canManage={canManage}
          canDelete={canDelete}
          onClose={() => setMembersOpen(false)}
          onChanged={() => onMembersChanged?.()}
          onDeleted={() => {
            setMembersOpen(false);
            onGroupDeleted?.();
          }}
        />
      )}
    </div>
  );
};

export default ChatThread;
